class CollectionFilters extends HTMLElement {
  constructor() {
    super();
    this.isLoading = false;
    this.productGrid = null;
    this.countElement = null;
    this.sortSelect = null;

    this.init();
  }

  connectedCallback() {
    this.setupEventListeners();
  }

  init() {
    this.productGrid = document.querySelector(".grid-products");
    this.countElement = document.querySelector(
      ".collection-filter__item--count",
    );
    this.sortSelect = document.querySelector("#SortBy");
  }

  setupEventListeners() {
    // Écouter les changements de tri
    if (this.sortSelect) {
      this.sortSelect.addEventListener(
        "change",
        this.handleSortChange.bind(this),
      );
    }
  }

  async handleSortChange(event) {
    const sortValue = event.target.value;
    const currentUrl = new URL(window.location);

    // Préserver tous les paramètres existants et ajouter/modifier le tri
    const searchParams = new URLSearchParams(currentUrl.search);
    searchParams.set("sort_by", sortValue);

    await this.applyFiltersFromParams(searchParams);
  }

  async applyFiltersFromParams(searchParams) {
    if (this.isLoading) return;

    this.isLoading = true;
    this.showLoading();

    try {
      const baseUrl = window.location.pathname;

      // Créer les paramètres pour la requête AJAX (avec view=ajax)
      const ajaxParams = new URLSearchParams(searchParams);
      ajaxParams.set("view", "ajax");
      const fetchUrl = `${baseUrl}?${ajaxParams.toString()}`;

      const response = await fetch(fetchUrl, {
        headers: {
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      this.updateProductGridFromHTML(html);

      // Mettre à jour l'URL du navigateur (sans view=ajax)
      const newUrl = searchParams.toString()
        ? `${baseUrl}?${searchParams.toString()}`
        : baseUrl;
      window.history.pushState({}, "", newUrl);

      // Mettre à jour le compteur de filtres actifs
      this.updateActiveFiltersCount(searchParams);
    } catch (error) {
      console.error("Erreur lors de l'application des filtres:", error);
    } finally {
      this.isLoading = false;
      this.hideLoading();
    }
  }

  updateProductGridFromHTML(html) {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    const newProductGrid = tempDiv.querySelector(".grid-products");
    if (newProductGrid && this.productGrid) {
      // Remplacer le contenu de la grille
      this.productGrid.innerHTML = newProductGrid.innerHTML;

      // Mettre à jour le compteur si présent
      const newCountElement = tempDiv.querySelector(
        ".collection-filter__item--count",
      );
      if (newCountElement && this.countElement) {
        this.countElement.innerHTML = newCountElement.innerHTML;
      }

      // Mettre à jour les pastilles de filtres actifs
      this.updateActiveFilterPills(tempDiv);

      // Déclencher un événement personnalisé
      this.dispatchEvent(
        new CustomEvent("collection-filtered", {
          detail: {
            productsCount: newProductGrid.children.length,
          },
        }),
      );
    }
  }

  updateActiveFilterPills(tempDiv) {
    // Chercher le nouveau contenu des filtres actifs dans la réponse AJAX
    const newActiveFilters = tempDiv.querySelector(
      ".collection-filter__item--active-filters",
    );
    const currentActiveFilters = document.querySelector(
      ".collection-filter__item--active-filters",
    );

    if (newActiveFilters) {
      // Si il y a de nouveaux filtres actifs, remplacer ou créer la section
      if (currentActiveFilters) {
        currentActiveFilters.innerHTML = newActiveFilters.innerHTML;
      } else {
        // Créer la section si elle n'existe pas
        const filterButton = document.querySelector(".insert-filters");
        if (filterButton) {
          filterButton.insertAdjacentHTML(
            "afterend",
            newActiveFilters.outerHTML,
          );
        }
      }
    } else {
      // Si il n'y a plus de filtres actifs, supprimer la section
      if (currentActiveFilters) {
        currentActiveFilters.remove();
      }
    }
  }

  updateActiveFiltersCount(searchParams) {
    const filterButton = document.querySelector(
      ".js-drawer-open-collection-filters",
    );
    if (!filterButton) return;

    // Compter les filtres actifs (exclure sort_by et view)
    let activeFiltersCount = 0;
    for (const [key, value] of searchParams.entries()) {
      if (key.startsWith("filter.") && value) {
        activeFiltersCount++;
      }
    }

    // Mettre à jour l'affichage du bouton
    const existingCount = filterButton.querySelector(".filter-count");
    if (existingCount) {
      existingCount.remove();
    }

    if (activeFiltersCount > 0) {
      filterButton.classList.add("btn--tertiary-active");
      const countSpan = document.createElement("span");
      countSpan.className = "filter-count";
      countSpan.textContent = ` (${activeFiltersCount})`;
      filterButton.appendChild(countSpan);
    } else {
      filterButton.classList.remove("btn--tertiary-active");
    }
  }

  showLoading() {
    if (this.productGrid) {
      this.productGrid.style.opacity = "0.5";
      this.productGrid.style.pointerEvents = "none";
    }
  }

  hideLoading() {
    if (this.productGrid) {
      this.productGrid.style.opacity = "1";
      this.productGrid.style.pointerEvents = "auto";
    }
  }

  // Méthode publique pour appliquer des filtres depuis le quiz
  async applyQuizFilters(quizData) {
    const filterMapping = {
      degustation: "filter.p.m.custom.usage_du_matcha=Dégustation",
      latte: "filter.p.m.custom.usage_du_matcha=Quotidien",
      cadeau: "filter.p.m.custom.usage_du_matcha=Cadeau",
      quotidien: "filter.p.m.custom.gamme_de_matcha=Quotidien & accessible",
      premium: "filter.p.m.custom.gamme_de_matcha=Premium & Raffiné",
      prestigieux:
        "filter.p.m.custom.gamme_de_matcha=Prestigieux & Exceptionnel",
    };

    const choice = quizData.step2or3;
    const filterParam = filterMapping[choice];

    if (!filterParam) {
      console.warn("Aucun filtre applicable pour ce choix de quiz:", choice);
      return;
    }

    // Récupérer les paramètres actuels de l'URL
    const currentUrl = new URL(window.location);
    const searchParams = new URLSearchParams(currentUrl.search);

    // Ajouter le nouveau filtre
    const [filterKey, filterValue] = filterParam.split("=");
    searchParams.set(filterKey, decodeURIComponent(filterValue));

    // Appliquer les filtres
    await this.applyFiltersFromParams(searchParams);
  }

  // Méthode publique pour récupérer les produits filtrés pour la modale quiz
  async getFilteredProductsForQuiz(quizData) {
    const filterMapping = {
      degustation: "filter.p.m.custom.usage_du_matcha=Dégustation",
      latte: "filter.p.m.custom.usage_du_matcha=Quotidien",
      cadeau: "filter.p.m.custom.usage_du_matcha=Cadeau",
      quotidien: "filter.p.m.custom.gamme_de_matcha=Quotidien & accessible",
      premium: "filter.p.m.custom.gamme_de_matcha=Premium & Raffiné",
      prestigieux:
        "filter.p.m.custom.gamme_de_matcha=Prestigieux & Exceptionnel",
    };

    const choice = quizData.step2or3;
    const filterParam = filterMapping[choice];

    if (!filterParam) {
      console.warn("Aucun filtre applicable pour ce choix de quiz:", choice);
      return [];
    }

    // Construire les paramètres de filtrage
    const searchParams = new URLSearchParams();
    const [filterKey, filterValue] = filterParam.split("=");
    searchParams.set(filterKey, decodeURIComponent(filterValue));

    // Ajouter les filtres de budget si présents
    if (quizData.budget) {
      switch (quizData.budget) {
        case "budget-25-50":
          searchParams.append("filter.v.price.gte", "25.00");
          searchParams.append("filter.v.price.lte", "50.00");
          break;
        case "budget-50-100":
          searchParams.append("filter.v.price.gte", "50.00");
          searchParams.append("filter.v.price.lte", "100.00");
          break;
        case "budget-100-200":
          searchParams.append("filter.v.price.gte", "100.00");
          searchParams.append("filter.v.price.lte", "200.00");
          break;
      }
    }

    try {
      const baseUrl = window.location.pathname;
      const ajaxParams = new URLSearchParams(searchParams);
      ajaxParams.set("view", "ajax");
      const fetchUrl = `${baseUrl}?${ajaxParams.toString()}`;

      const response = await fetch(fetchUrl, {
        headers: {
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = html;

      // Chercher les produits dans la réponse AJAX
      const productElements = tempDiv.querySelectorAll(
        ".grid-products > *:not(.promo-card__wrapper)",
      );
      return Array.from(productElements).slice(0, 6);
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des produits pour le quiz:",
        error,
      );
      return [];
    }
  }

  // Méthode publique pour supprimer un filtre
  async removeFilter(removeUrl) {
    // Récupérer les paramètres de l'URL de suppression
    const searchParams = new URLSearchParams(removeUrl.search);

    // Appliquer les filtres mis à jour
    await this.applyFiltersFromParams(searchParams);
  }
}

// Enregistrer le custom element
customElements.define("collection-filters", CollectionFilters);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector("a");
    if (facetLink) {
      facetLink.setAttribute("role", "button");
      facetLink.addEventListener("click", this.closeFilter.bind(this));
      facetLink.addEventListener("keyup", (event) => {
        event.preventDefault();
        if (event.code.toUpperCase() === "SPACE") this.closeFilter(event);
      });
    }
  }

  closeFilter(event) {
    event.preventDefault();
    const collectionFilters = document.querySelector("collection-filters");
    if (
      collectionFilters &&
      typeof collectionFilters.removeFilter === "function"
    ) {
      const link = event.currentTarget;
      const url = new URL(link.href);
      collectionFilters.removeFilter(url);
    }
  }
}

customElements.define("facet-remove", FacetRemove);
